import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Loader2, RotateCcw, X } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { Attribution } from "@/components/Provenance";

const List = ({ title, items, testid }) =>
  items?.length ? (
    <div data-testid={testid}>
      <div className="eyebrow mb-2">{title}</div>
      <ul className="space-y-1.5 text-sm leading-relaxed text-[var(--ink-soft)]">
        {items.map((t, i) => <li key={i} className="flex gap-2"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--accent)]" />{t}</li>)}
      </ul>
    </div>
  ) : null;

export const RoomAnalysis = ({ room }) => (
  <div className="mt-6 grid gap-6 border-t border-[var(--line)] pt-6 sm:grid-cols-2" data-testid={`room-analysis-${room.id}`}>
    {room.architecturalStyle && (
      <div className="sm:col-span-2">
        <div className="eyebrow mb-2">Architectural style</div>
        <p className="font-display text-xl" data-testid={`room-style-${room.id}`}>{room.architecturalStyle}</p>
      </div>
    )}
    <List title="Visible features" items={room.architecturalFeatures} testid={`room-features-${room.id}`} />
    <List title="Improvement opportunities" items={room.opportunities} testid={`room-opportunities-${room.id}`} />
    <List title="Potential maintenance concerns" items={room.maintenanceConcerns} testid={`room-maintenance-${room.id}`} />
    <List title="Preservation constraints" items={room.constraints} testid={`room-constraints-${room.id}`} />
  </div>
);

export const PhotoCard = ({ photo, rooms, vision, onMove, onCreateRoom, onGenerate, generating }) => {
  const [newName, setNewName] = useState(null);
  const notProperty = photo.isPropertyPhoto === false;
  const noAi = photo.provenance?.allowedUses?.aiReimagining === false;
  return (
    <figure className="group" data-testid={`photo-card-${photo.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden rounded-[2px] bg-[var(--sand)]">
        <img src={assetUrl(photo.url)} alt={photo.caption || photo.originalFilename} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
        {notProperty && (
          <span data-testid={`photo-not-property-${photo.id}`} className="absolute left-2 top-2 rounded-full bg-[var(--charcoal)]/80 px-2.5 py-1 text-[0.7rem] text-[var(--bone)]">Not a property photo</span>
        )}
      </div>
      <div className="mt-1.5"><Attribution provenance={photo.provenance} testid={`photo-attribution-${photo.id}`} /></div>
      {noAi && <p className="mt-1 text-xs text-[#B42318]" data-testid={`photo-no-ai-${photo.id}`}>This photo&apos;s license doesn&apos;t allow AI reimagining.</p>}
      {photo.caption && <figcaption className="mt-2 line-clamp-2 text-xs text-[var(--stone-dark)]">{photo.caption}</figcaption>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {vision ? (
          <Link to={`/v/${vision.id}`} className="btn-primary !min-h-[44px] !px-4 text-sm" data-testid={`open-vision-${photo.id}`}>Open vision</Link>
        ) : (
          <button className="btn-primary !min-h-[44px] !px-4 text-sm" disabled={notProperty || noAi || generating} onClick={() => onGenerate(photo)} data-testid={`generate-vision-${photo.id}`}>
            {generating ? <Loader2 size={15} className="animate-spin" /> : null} Generate vision
          </button>
        )}
        <select
          aria-label="Move to room"
          value={photo.roomId || ""}
          onChange={(e) => (e.target.value === "__new" ? setNewName("") : onMove(photo, e.target.value))}
          className="min-h-[44px] max-w-[11rem] rounded-full border border-[var(--line)] bg-transparent px-3 text-sm"
          data-testid={`move-photo-select-${photo.id}`}
        >
          <option value="" disabled>Move to room…</option>
          {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          <option value="__new">+ New room…</option>
        </select>
      </div>
      {newName !== null && (
        <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (newName.trim()) { onCreateRoom(photo, newName.trim()); setNewName(null); } }}>
          <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Room name" className="field !min-h-[44px]" data-testid={`new-room-name-input-${photo.id}`} />
          <button className="btn-primary !min-h-[44px] !px-4 text-sm" data-testid={`new-room-save-${photo.id}`}>Save</button>
          <button type="button" className="btn-quiet" onClick={() => setNewName(null)} aria-label="Cancel"><X size={16} /></button>
        </form>
      )}
    </figure>
  );
};

export const UploadTile = ({ item, onRetry, onDismiss }) => (
  <div className="relative" data-testid={`upload-tile-${item.key}`}>
    <div className="relative aspect-[4/3] overflow-hidden rounded-[2px] bg-[var(--sand)]">
      {item.preview && <img src={item.preview} alt="" className={`h-full w-full object-cover ${item.status === "done" ? "" : "opacity-60"}`} />}
      {item.status === "uploading" && (
        <div className="absolute inset-x-0 bottom-0 h-1 bg-[var(--charcoal)]/15">
          <div className="h-full bg-[var(--charcoal)] transition-[width] duration-200" style={{ width: `${item.progress}%` }} data-testid={`upload-progress-${item.key}`} />
        </div>
      )}
      {(item.status === "error" || item.status === "rejected") && (
        <div className="absolute inset-0 flex flex-col items-start justify-end gap-2 bg-[var(--bone)]/85 p-3">
          <p className="flex items-start gap-1.5 text-xs leading-snug text-[#B42318]" data-testid={`upload-error-${item.key}`}><AlertCircle size={14} className="mt-px shrink-0" />{item.error}</p>
          <div className="flex gap-2">
            {item.status === "error" && <button className="btn-primary !min-h-[44px] !px-4 text-xs" onClick={() => onRetry(item)} data-testid={`upload-retry-${item.key}`}><RotateCcw size={13} /> Retry</button>}
            <button className="btn-quiet text-xs" onClick={() => onDismiss(item)} data-testid={`upload-dismiss-${item.key}`}>Dismiss</button>
          </div>
        </div>
      )}
    </div>
    <div className="mt-1.5 truncate text-xs text-[var(--stone-dark)]">{item.file.name}{item.status === "uploading" ? ` · ${item.progress}%` : ""}</div>
  </div>
);
